import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import PageHeader from '../components/layout/PageHeader';
import { staffTabClass, staffCardClass } from '../components/layout/staffPageStyles';
import {
  BarChart3, TrendingUp, Users, Building2,
  DollarSign, Wrench, ShoppingBag, RefreshCw, Wallet
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['var(--theme-primary)', '#06b6d4', '#f59e0b', '#ef4444', '#10b981', '#ec4899', '#3b82f6', '#f97316', '#14b8a6', '#a855f7'];

// CONVENTION D'ARRONDI QUI FAIT FOI (affichage seul, aucune pièce comptable modifiée) :
// chaque total MENSUEL est arrondi à 2 décimales côté backend (= les barres du graphe).
// La carte « CA » = SOMME de ces montants mensuels, elle-même arrondie à 2 décimales.
// => carte == somme des barres du graphe == total historique, à l'identique (pas de dérive 0,01 €).
const round2 = (x) => Math.round(((Number(x) || 0) + Number.EPSILON) * 100) / 100;
const eur2 = (x) => round2(x).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Début de l'exploitation courante (miroir backend OPERATIONAL_START). Sert au libellé de période.
const OPERATIONAL_START_LABEL = '1ᵉʳ septembre 2026';

const StatsPage = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('revenue');
  // Vue HISTORIQUE distincte (archives incluses) vs EXPLOITATION COURANTE (depuis le 1er sept. 2026).
  const [historique, setHistorique] = useState(false);
  const [revenue, setRevenue] = useState([]);
  const [repairs, setRepairs] = useState(null);
  const [b2b, setB2b] = useState(null);
  const [clients, setClients] = useState(null);

  const api = axios.create({ baseURL: `${API_URL}/api`, headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      // Le paramètre historique ne concerne que le CA et les clients (réparations/B2B inchangés).
      const hq = historique ? '?historique=true' : '';
      try {
        const [rev, rep, b, cl] = await Promise.all([
          api.get(`/stats/revenue${hq}`),
          api.get('/stats/repairs'),
          api.get('/stats/b2b'),
          api.get(`/stats/clients${hq}`),
        ]);
        setRevenue(rev.data);
        setRepairs(rep.data);
        setB2b(b.data);
        setClients(cl.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [historique]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    { key: 'revenue', icon: DollarSign, label: t('stats.revenue') },
    { key: 'repairs', icon: Wrench, label: t('stats.repairs') },
    { key: 'b2b', icon: Building2, label: t('stats.b2b') },
    { key: 'clients', icon: Users, label: t('stats.clients') },
  ];

  // CA JUSTIFIÉ = somme des totaux mensuels (ventes réellement encaissées, hors acomptes & retraits),
  // chacun déjà arrondi 2 déc (= barres du graphe), puis arrondi 2 déc.
  const totalRevenue = round2(revenue.reduce((s, m) => s + (m.total || 0), 0));
  // ACOMPTES ENCAISSÉS (avances sur commandes/réparations non finalisées) — affichés SÉPARÉMENT, jamais dans le CA.
  const totalAcomptes = round2(revenue.reduce((s, m) => s + (m.acomptes || 0), 0));
  const totalRepairs = repairs?.monthly?.reduce((s, m) => s + m.count, 0) || 0;
  const totalB2BRevenue = round2(b2b?.monthly_comparison?.reduce((s, m) => s + (m.b2b || 0), 0) || 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    return (
      <div className="bg-gray-900/95 border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
        <p className="text-white font-semibold mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="flex justify-between gap-4">
            <span>{p.name} :</span>
            <span className="font-bold">{typeof p.value === 'number' ? p.value.toLocaleString('fr-FR') : p.value}{p.unit || ' €'}</span>
          </p>
        ))}
      </div>
    );
  };

  if (loading) return (
    <Layout>
      <PageHeader title={t('stats.title')} backTo="/dashboard" icon={BarChart3} testId="stats-page-header" />
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-[var(--theme-primary)] animate-spin" />
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="space-y-6 max-w-[1440px] mx-auto" data-testid="stats-page">
        <PageHeader
          title={t('stats.title')}
          subtitle={historique
            ? 'Vue HISTORIQUE — 12 mois, anciennes ventes archivées incluses (consultation)'
            : `Exploitation courante — depuis le ${OPERATIONAL_START_LABEL}`}
          backTo="/dashboard" icon={BarChart3} testId="stats-page-header" />

        {/* Bascule Exploitation courante / Historique complet */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="inline-flex rounded-xl bg-white/5 border border-white/10 p-1" data-testid="stats-view-toggle">
            <button
              type="button"
              onClick={() => setHistorique(false)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${!historique ? 'bg-[var(--theme-primary)] text-white' : 'text-gray-400 hover:text-white'}`}
              data-testid="stats-view-operationnel"
            >
              Exploitation courante
            </button>
            <button
              type="button"
              onClick={() => setHistorique(true)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${historique ? 'bg-amber-500 text-white' : 'text-gray-400 hover:text-white'}`}
              data-testid="stats-view-historique"
            >
              Historique complet (archives)
            </button>
          </div>
          <span className="text-xs text-gray-500" data-testid="stats-period-label">
            {historique
              ? 'CA justifié = ventes encaissées ; acomptes (avances) affichés à part. 12 mois, archives incluses.'
              : `CA justifié = ventes encaissées depuis le ${OPERATIONAL_START_LABEL} ; acomptes (avances) affichés à part.`}
          </span>
        </div>

        {/* KPI Cards — CA justifié et Acomptes encaissés présentés SÉPARÉMENT */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <KPICard
            icon={DollarSign}
            label={historique ? 'CA ventes historique (12 mois)' : `CA ventes — depuis le ${OPERATIONAL_START_LABEL}`}
            value={`${eur2(totalRevenue)} €`}
            sub="Ventes encaissées (hors acomptes & retraits)"
            color="text-emerald-400" bg="bg-emerald-500/10" />
          <KPICard
            icon={Wallet}
            label="Acomptes encaissés (avances)"
            value={`${eur2(totalAcomptes)} €`}
            sub="Commandes/réparations en cours — pas encore du CA"
            color="text-violet-300" bg="bg-violet-500/10" />
          <KPICard icon={Wrench} label={t('stats.totalRepairs')} value={totalRepairs} color="text-blue-400" bg="bg-blue-500/10" />
          <KPICard icon={Building2} label={t('stats.b2bRevenue')} value={`${eur2(totalB2BRevenue)} €`} color="text-[var(--theme-primary)]" bg="bg-[rgba(var(--theme-primary-rgb),0.1)]" />
          <KPICard icon={Users} label="Top client (CA)" value={clients?.top_clients?.[0]?.client || '-'} sub={clients?.top_clients?.[0] ? `${eur2(clients.top_clients[0].revenue)} €` : ''} color="text-amber-400" bg="bg-amber-500/10" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white/5 rounded-xl p-1 overflow-x-auto border border-white/10" data-testid="stats-tabs">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={staffTabClass(activeTab === tab.key)}
              data-testid={`stats-tab-${tab.key}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'revenue' && <RevenueTab data={revenue} tooltip={CustomTooltip} />}
        {activeTab === 'repairs' && repairs && <RepairsTab data={repairs} tooltip={CustomTooltip} />}
        {activeTab === 'b2b' && b2b && <B2BTab data={b2b} tooltip={CustomTooltip} />}
        {activeTab === 'clients' && clients && <ClientsTab data={clients} tooltip={CustomTooltip} />}
      </div>
    </Layout>
  );
};

const KPICard = ({ icon: Icon, label, value, sub, color, bg }) => (
  <div className={`${bg} border border-white/10 rounded-xl p-4`} data-testid="stats-kpi-card">
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 ${color}`} />
      <span className="text-xs text-gray-400 uppercase tracking-wide">{label}</span>
    </div>
    <p className={`text-lg lg:text-xl font-bold ${color}`}>{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className={`${staffCardClass} p-5`} data-testid="stats-chart-card">
    <h3 className="text-sm font-semibold text-gray-300 mb-4">{title}</h3>
    {children}
  </div>
);

// ========== REVENUE TAB ==========
// Infobulle revenu : MOYEN de paiement (Espèces / Carte / Autre) + sous-répartition
// de la carte par PRESTATAIRE (SumUp / Stripe / Revolut / prestataire inconnu).
const RevenueTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const m = payload[0]?.payload || {};
  const prov = m.providers || {};
  const eur = (v) => `${(Number(v) || 0).toLocaleString('fr-FR')} €`;
  const rows = [
    ['Espèces', m.especes, '#10b981'],
    ['Carte', m.cb, '#3b82f6'],
    ['Autre', m.autre, '#f59e0b'],
  ];
  const provRows = [
    ['SumUp', prov.sumup],
    ['Stripe', prov.stripe],
    ['Revolut', prov.revolut],
    ['Prestataire inconnu', prov.inconnu],
  ].filter(([, v]) => Math.abs(Number(v) || 0) > 0.005);
  return (
    <div className="bg-gray-900/95 border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl min-w-[180px]">
      <p className="text-white font-semibold mb-1">{label}</p>
      {rows.map(([name, val, color]) => (
        <p key={name} style={{ color }} className="flex justify-between gap-4">
          <span>{name} :</span><span className="font-bold">{eur(val)}</span>
        </p>
      ))}
      <p className="flex justify-between gap-4 text-white border-t border-white/10 mt-1 pt-1">
        <span>Total :</span><span className="font-bold">{eur(m.total)}</span>
      </p>
      {provRows.length > 0 && (
        <div className="mt-1 pt-1 border-t border-white/10 text-gray-400">
          <p className="mb-0.5">dont carte, par prestataire :</p>
          {provRows.map(([name, val]) => (
            <p key={name} className="flex justify-between gap-4">
              <span>{name} :</span><span>{eur(val)}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

const RevenueTab = ({ data, tooltip }) => {
  const { t } = useLanguage();
  // Répartition par MOYEN sur la période (Espèces / Carte / Autre)
  const paymentSplit = data.reduce((acc, m) => {
    acc[0].value += m.especes || 0;
    acc[1].value += m.cb || 0;
    acc[2].value += m.autre || 0;
    return acc;
  }, [{ name: 'Espèces', value: 0 }, { name: 'Carte', value: 0 }, { name: 'Autre', value: 0 }]);
  // Répartition de la carte par PRESTATAIRE sur la période (prouvé vs inconnu)
  const providerSplit = data.reduce((acc, m) => {
    const p = m.providers || {};
    acc.sumup += p.sumup || 0; acc.stripe += p.stripe || 0;
    acc.revolut += p.revolut || 0; acc.inconnu += p.inconnu || 0;
    return acc;
  }, { sumup: 0, stripe: 0, revolut: 0, inconnu: 0 });
  const providerRows = [
    ['SumUp', providerSplit.sumup], ['Stripe', providerSplit.stripe],
    ['Revolut', providerSplit.revolut], ['Prestataire inconnu', providerSplit.inconnu],
  ].filter(([, v]) => Math.abs(v) > 0.005);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="stats-revenue-tab">
      <ChartCard title={t('stats.monthlyRevenue')}>
        <div className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="month" tick={{ fill: '#9ca3af', fontSize: 11 }} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
              <Tooltip content={<RevenueTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="especes" name="Espèces" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="cb" name="Carte" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="autre" name="Autre" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      <ChartCard title={t('stats.revenueByPayment')}>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={paymentSplit.filter(p => p.value > 0)} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
              {paymentSplit.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
            </Pie>
            <Tooltip formatter={(v) => `${v.toLocaleString('fr-FR')} €`} />
          </PieChart>
        </ResponsiveContainer>
        {providerRows.length > 0 && (
          <div className="mt-2 text-xs text-gray-400 border-t border-white/10 pt-2">
            <p className="mb-1 text-gray-300">Carte — par prestataire :</p>
            {providerRows.map(([name, val]) => (
              <p key={name} className="flex justify-between">
                <span>{name}</span>
                <span className="font-semibold">{val.toLocaleString('fr-FR')} €</span>
              </p>
            ))}
          </div>
        )}
      </ChartCard>

      <ChartCard title="Évolution du CA total">
        <div className="lg:col-span-3">
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="gradCA" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--theme-primary)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--theme-primary)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="month" tick={{ fill: '#9ca3af', fontSize: 11 }} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
              <Tooltip content={tooltip} />
              <Area type="monotone" dataKey="total" name="CA Total" stroke="var(--theme-primary)" fill="url(#gradCA)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
    </div>
  );
};

// ========== REPAIRS TAB ==========
const MagasinRepairsBreakdown = ({ byMagasin }) => {
  if (!byMagasin?.M1) return null;
  return (
    <div className={`${staffCardClass} p-5 lg:col-span-3`} data-testid="stats-magasin-breakdown">
      <h3 className="text-sm font-semibold text-white mb-4">En cours par magasin (transparence legacy)</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div className="rounded-lg border border-cyan-500/20 p-4">
          <div className="font-bold text-cyan-300 mb-2">M1</div>
          <p className="text-gray-500">{byMagasin.M1.repairs_in_progress_explicit ?? 0} M1 explicites</p>
          <p className="text-gray-500">{byMagasin.M1.repairs_in_progress_legacy ?? 0} legacy</p>
          <p className="text-white font-semibold mt-1">Total : {byMagasin.M1.repairs_in_progress ?? 0}</p>
        </div>
        <div className="rounded-lg border border-purple-500/20 p-4">
          <div className="font-bold text-purple-300 mb-2">M2</div>
          <p className="text-gray-500">{byMagasin.M2?.repairs_in_progress_explicit ?? 0} M2 explicites</p>
          <p className="text-white font-semibold mt-1">Total : {byMagasin.M2?.repairs_in_progress ?? 0}</p>
        </div>
      </div>
    </div>
  );
};

const RepairsTab = ({ data, tooltip }) => {
  const { t } = useLanguage();
  return (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="stats-repairs-tab">
    <MagasinRepairsBreakdown byMagasin={data.by_magasin} />
    <ChartCard title={t('stats.monthly')}>
      <div className="lg:col-span-2">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data.monthly} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
            <XAxis dataKey="month" tick={{ fill: '#9ca3af', fontSize: 11 }} />
            <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
            <Tooltip content={(p) => tooltip({ ...p, payload: p.payload?.map(pp => ({ ...pp, unit: '' })) })} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="standard" name="Standard" fill="#06b6d4" radius={[4, 4, 0, 0]} />
            <Bar dataKey="b2b" name="B2B" fill="var(--theme-primary)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>

    <ChartCard title={t('stats.repairsByStatus')}>
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie data={data.by_status} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} dataKey="count" nameKey="status" label={({ status, percent }) => `${status} ${(percent * 100).toFixed(0)}%`}>
            {data.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>

    <ChartCard title={t('stats.repairsByBrand')}>
      <div className="lg:col-span-3">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.by_brand} layout="vertical" margin={{ top: 5, right: 20, left: 50, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
            <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 11 }} />
            <YAxis dataKey="brand" type="category" tick={{ fill: '#9ca3af', fontSize: 11 }} width={80} />
            <Tooltip content={(p) => tooltip({ ...p, payload: p.payload?.map(pp => ({ ...pp, unit: '' })) })} />
            <Bar dataKey="count" name="Réparations" fill="#06b6d4" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  </div>
  );
};

// ========== B2B TAB ==========
const B2BTab = ({ data, tooltip }) => {
  const { t } = useLanguage();
  return (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="stats-b2b-tab">
    <ChartCard title="CA B2B vs CA Total (mensuel)">
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={data.monthly_comparison} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <defs>
            <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradB2B" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--theme-primary)" stopOpacity={0.3} />
              <stop offset="95%" stopColor="var(--theme-primary)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
          <XAxis dataKey="month" tick={{ fill: '#9ca3af', fontSize: 11 }} />
          <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
          <Tooltip content={tooltip} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Area type="monotone" dataKey="total" name="CA Total" stroke="#06b6d4" fill="url(#gradTotal)" strokeWidth={2} />
          <Area type="monotone" dataKey="b2b" name="CA B2B" stroke="var(--theme-primary)" fill="url(#gradB2B)" strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>

    <ChartCard title={t('stats.b2bByCompany')}>
      {data.top_companies.length === 0 ? (
        <p className="text-gray-500 text-center py-8">{t('stats.noData')}</p>
      ) : (
        <div className="space-y-3">
          {data.top_companies.map((c, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg" data-testid={`stats-b2b-company-${i}`}>
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-[rgba(var(--theme-primary-rgb),0.2)] text-[var(--theme-primary)] text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <div>
                  <p className="text-white text-sm font-medium">{c.company}</p>
                  <p className="text-xs text-gray-500">{c.repairs} réparation{c.repairs > 1 ? 's' : ''}</p>
                </div>
              </div>
              <span className="text-emerald-400 font-bold text-sm">{eur2(c.revenue)} €</span>
            </div>
          ))}
        </div>
      )}
    </ChartCard>
  </div>
  );
};

// ========== CLIENTS TAB ==========
const ClientsTab = ({ data, tooltip }) => {
  const { t } = useLanguage();
  return (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="stats-clients-tab">
    <ChartCard title={t('stats.clientsByType')}>
      {data.top_clients.length === 0 ? (
        <p className="text-gray-500 text-center py-8">{t('stats.noData')}</p>
      ) : (
        <div className="space-y-2">
          {data.top_clients.map((c, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg" data-testid={`stats-client-${i}`}>
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-300 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <div>
                  <p className="text-white text-sm font-medium">{c.client}</p>
                  <p className="text-xs text-gray-500">{c.transactions} transaction{c.transactions > 1 ? 's' : ''}</p>
                </div>
              </div>
              <span className="text-emerald-400 font-bold text-sm">{eur2(c.revenue)} €</span>
            </div>
          ))}
        </div>
      )}
    </ChartCard>

    <ChartCard title={t('stats.registrations')}>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data.new_clients_monthly} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
          <XAxis dataKey="month" tick={{ fill: '#9ca3af', fontSize: 11 }} />
          <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
          <Tooltip content={(p) => tooltip({ ...p, payload: p.payload?.map(pp => ({ ...pp, unit: '' })) })} />
          <Bar dataKey="count" name="Nouveaux clients" fill="#f59e0b" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  </div>
  );
};

export default StatsPage;
